import { loadInlineStyles, TmElement } from '@thiememeulenhoff/lit-sdk';
import { translate } from '@thiememeulenhoff/translations';
import { IconName } from '@thiememeulenhoff/types';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { tagName } from '../../helpers';
import '../slider/slider.component';
import { PlayerError, PlayerState } from './read-speaker-state.enum';
import { ReadSpeakerEvent, ReadSpeakerService } from './read-speaker.service';
import { styles } from './read-speaker.styles';

const iconMap: Record<PlayerState, IconName> = {
  init: 'voiceover',
  loading: 'loading',
  playing: 'pause',
  paused: 'play',
  stopped: 'voiceover',
  speed: 'gauge',
};

const tag = tagName('read-speaker');

@customElement(tag)
export class ReadSpeakerComponent extends TmElement {
  readonly #readSpeakerService = ReadSpeakerService.get();

  // Quick and dirty ID for the component instance
  @property({ type: String, reflect: true })
  elementId = Math.random().toString(36).slice(2, 7);

  @property({ type: String, attribute: 'read-class', reflect: true })
  readClass = 'readthis';

  @property({ type: Boolean, reflect: true, attribute: 'box-shadow' })
  boxShadow = true;

  @property({ attribute: 'scroll-container' })
  scrollContainer = '';

  @property({ type: String, reflect: true })
  language = 'nl_nl';

  @state()
  sliderValue = 50;

  @state()
  showSlider = false;

  @state()
  state: PlayerState = PlayerState.Init;

  protected override createRenderRoot() {
    return this; // https://lit.dev/docs/components/shadow-dom/#implementing-createrenderroot
  }

  override render() {
    loadInlineStyles(this, styles);
    const classes = {
      init: this.state === PlayerState.Init,
      loading: this.state === PlayerState.Loading,
      playing: this.state === PlayerState.Playing,
      paused: this.state === PlayerState.Paused,
      stopped: this.state === PlayerState.Stopped,
      speed: this.state === PlayerState.Speed,
    };

    const togglesWhenPlayer =
      this.state === PlayerState.Playing || this.state === PlayerState.Paused
        ? html`<button
              @click=${this.#speedToggle}
              class="button settings-button"
              type="button"
            >
              <a-icon class="settings-icon" name="gauge"></a-icon>
            </button>
            <div ?hidden="${!this.showSlider}">
              <a-slider
                value="${this.sliderValue}"
                @change="${this.#speedChange}"
                label=""
                step="5"
                min="5"
                max="200"
              ></a-slider>
            </div>`
        : nothing;

    const readSpeakerButtonTemplate = html`<div
      id="${this.#id}"
      class="rs_skip rsbtn rs_preserve"
    >
      <a
        rel="nofollow"
        id="${this.#id}-play"
        class="rsbtn_play"
        accesskey="L"
        title="Laat de tekst voorlezen met ReadSpeaker"
        href="https://app-eu.readspeaker.com/cgi-bin/rsent?customerid=5797&amp;lang=${
          this.language
        }&amp;readclass=${this.#uniqueReadClass}"
      >
        <span class="rsbtn_left rsimg rspart">
          <span class="rsbtn_text"><span>Lees voor</span></span>
        </span>
        <span class="rsbtn_right rsimg rsplay rspart"></span>
      </a>
    </div>`;

    return html`
      <div class="rs_skip read-speaker ${classMap(classes)}">
        <button
          @click=${this.#toggle}
          class="button main-button"
          type="button"
          aria-label="${translate('button.readspeaker.aria_label')}"
        >
          <a-icon class="main-icon" name=${iconMap[this.state]}></a-icon>
        </button>
        ${togglesWhenPlayer}
        <button @click=${this.#stop} class="button close-button" type="button">
          <a-icon class="close-icon" name="close"></a-icon>
        </button>
      </div>
      ${readSpeakerButtonTemplate}
    `;
  }

  get #id() {
    return `read-speaker-button-${this.elementId}`;
  }

  get #uniqueReadClass() {
    return `${this.readClass}-${this.elementId}`;
  }

  #toggle() {
    if (this.state === PlayerState.Playing) {
      this.#pause();
      return;
    }

    this.#play();
  }

  #playPending = false;

  #playTimeoutId?: ReturnType<typeof setTimeout>;

  #play() {
    // #play()'s work below is deferred (see comment further down), which
    // leaves a window where `state` hasn't reached Playing yet. Without this
    // guard, a second click landing in that window would re-enter #play()
    // via #toggle() and queue a second registration/play call.
    if (this.#playPending) {
      return;
    }
    this.#playPending = true;

    const wasPaused = this.state === PlayerState.Paused;
    if (!wasPaused) {
      this.state = PlayerState.Loading;
    }

    // Selecting a question and pressing play can fire from the same click
    // (it bubbles into the card's own selection handler, which may navigate
    // and trigger change detection in the same task). Defer to the next task
    // so that settles first, matching two separate clicks.
    this.#playTimeoutId = setTimeout(() => {
      this.#playPending = false;
      this.#playTimeoutId = undefined;
      const contentElement = this.#findContentElement();
      // Tag the specific content element with a unique class so the external
      // ReadSpeaker library targets only this instance's content.
      contentElement?.classList.add(this.#uniqueReadClass);

      // Re-pair the scroll container on every play so it can't drift out of
      // sync with the content pairing computed above.
      if (this.scrollContainer !== '') {
        this.#registerScrollContainer();
      }

      this.#readSpeakerService.register(
        this.querySelector(`#${this.#id}`) as HTMLElement,
        contentElement && this.#contentContainer(contentElement),
        this.#handleReadSpeakerEvent.bind(this),
      );

      if (wasPaused) {
        this.#readSpeakerService.resume();
      } else {
        this.#readSpeakerService.play(this.#id);
      }

      this.sliderValue = this.#readSpeakerService.speed;
    }, 0);
  }

  #pause() {
    this.#readSpeakerService.pause();
  }

  #stop() {
    this.#readSpeakerService.stop();
  }

  #speedToggle() {
    this.showSlider = !this.showSlider;
  }

  #speedChange(value: CustomEvent) {
    this.sliderValue = Number(value.detail);
    this.#readSpeakerService.speed = this.sliderValue;
  }

  #handleReadSpeakerEvent(event: ReadSpeakerEvent) {
    switch (event.event) {
      case PlayerState.Playing:
      case PlayerState.Paused:
      case PlayerState.Stopped:
        this.state = event.event;
        this.dispatchLocalEvent({
          element: this,
          eventName: event.event,
        });
        break;
      case PlayerError.AudioLoadError:
      case PlayerError.AudioNoSound:
        this.dispatchLocalEvent({
          element: this,
          eventName: PlayerState.Stopped,
        });
        throw Error(`ReadSpeaker audio error: ${event.event}`);
      default:
        break;
    }
  }

  #findScrollParent(element: Element): Element | null {
    if (element.matches(this.scrollContainer)) {
      return element;
    }

    const shadowElement = element.shadowRoot?.querySelector(
      this.scrollContainer,
    );
    if (shadowElement) {
      return shadowElement;
    }

    if (element.parentElement) {
      return this.#findScrollParent(element.parentElement);
    }

    return null;
  }

  #contentContainer(contentElement: Element): Element | null | undefined {
    // Importing the TheoryComponent is not possible because of circular dependencies, so we try to select
    // the element that contains the theory. If this succeeds the contentElement is a TheoryComponent
    const innerElement = contentElement.shadowRoot
      ?.querySelector('#view')
      ?.shadowRoot?.querySelector('div.readthis');

    return innerElement ?? contentElement;
  }

  override onCreate = () => {
    if (this.scrollContainer !== '') {
      window.setTimeout(() => this.#registerScrollContainer());
    }
  };

  #findContentElement(): Element | null {
    // This function returns the correct content element which has to be read

    // First we check if there is only one content element with the read-class
    // If so, a proximity-based lookup is not necessary, we return the single element
    const contentNodes = document.querySelectorAll(`.${this.readClass}`);
    if (contentNodes.length === 1) {
      return contentNodes[0];
    }

    // Proximity-based lookups (closest/:has) can grab another instance's
    // content when multiple widgets share a read-class under one ancestor.
    // Widgets and their content are always rendered in matching document
    // order, so scan forward from this widget and take the first same-class
    // content element reached before the next same-class widget. This keeps
    // a missing content element scoped to that one widget instead of
    // shifting every pairing after it.
    const isSameClassWidget = (element: Element) =>
      element.tagName === 'A-READ-SPEAKER' &&
      (element as ReadSpeakerComponent).readClass === this.readClass;

    const nodes = document.querySelectorAll(
      `a-read-speaker, .${this.readClass}`,
    );
    const myIndex = Array.prototype.indexOf.call(nodes, this);

    for (let index = myIndex + 1; index < nodes.length; index += 1) {
      const node = nodes[index];
      if (isSameClassWidget(node)) {
        return null;
      }
      if (node.classList.contains(this.readClass)) {
        return node;
      }
    }

    return null;
  }

  #registerScrollContainer() {
    const contentElement = this.#findContentElement();

    if (!(contentElement instanceof Element)) {
      return;
    }

    const scrollContainer = this.#findScrollParent(contentElement);
    const contentContainer = this.#contentContainer(contentElement);
    if (
      scrollContainer instanceof Element &&
      contentContainer instanceof Element
    ) {
      this.#readSpeakerService.registerScrollContainer(
        this.elementId,
        scrollContainer,
        contentContainer,
      );
    }
  }

  override onDestroy = () => {
    clearTimeout(this.#playTimeoutId);
    this.#readSpeakerService.unregisterScrollContainer(this.elementId);
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'a-read-speaker': ReadSpeakerComponent;
  }
}
